import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ShoppingBag,
  Check,
  BookOpen,
  Infinity as InfinityIcon,
  Sparkles,
  ExternalLink,
} from "lucide-react";
import { findProduct } from "../data/products";
import { useCart, formatEuro } from "../context/CartContext";
import LibrarySection from "../components/LibrarySection";

/**
 * Pagina prodotto ufficiale — TIME · Il Libro del Tempo.
 *
 * Primo prodotto acquistabile dell'ecosistema TIME Inside.
 * Riutilizza:
 *   • copertina ufficiale in /covers/time-il-libro-del-tempo.png
 *   • stile grafico coerente con TIME Inside (dark, minimal, seven-seg accent)
 *   • dati centralizzati da /data/products.js
 */
export default function TimeIlLibroDelTempoPage() {
  const product = findProduct("time-il-libro-del-tempo");
  const navigate = useNavigate();
  const { addItem, items } = useCart();

  if (!product) return null;

  const alreadyInCart = items.some((it) => it.slug === product.slug);

  const handleBuyNow = () => {
    addItem(product, 1);
    navigate("/carrello");
  };

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-16 sm:py-20 max-w-6xl mx-auto">
      <Link
        to="/prodotti/libri"
        data-testid="time-book-back-link"
        className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-white/40 hover:text-white/80 transition mb-10"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
        Torna a Libreria
      </Link>

      {/* HERO */}
      <div
        data-testid="time-book-hero"
        className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-10 lg:gap-16 items-start"
      >
        {/* Cover — copertina ufficiale, mai sostituita */}
        <div className="relative">
          <div className="relative rounded-2xl overflow-hidden bg-white/[0.02] border border-white/5 shadow-[0_25px_80px_-20px_rgba(0,0,0,0.7)]">
            <img
              src={product.cover}
              alt={`${product.title} — ${product.subtitle}`}
              data-testid="time-book-cover"
              className="w-full h-auto object-cover"
              draggable="false"
            />
          </div>
        </div>

        {/* Info principale */}
        <div>
          <div className="text-[11px] tracking-[0.32em] uppercase text-emerald-400/80 mb-4">
            Prodotti · Libreria · Edizione digitale
          </div>
          <h1
            data-testid="time-book-title"
            className="text-4xl sm:text-5xl md:text-6xl font-extralight tracking-tight text-white leading-[1.02]"
          >
            {product.title}
            <span className="block text-2xl sm:text-3xl md:text-4xl text-white/70 font-light mt-2">
              {product.subtitle}
            </span>
          </h1>

          <p
            data-testid="time-book-tagline"
            className="mt-6 text-lg sm:text-xl italic text-emerald-300/80 font-light leading-relaxed"
          >
            Un libro che cresce insieme al Tempo.
          </p>

          {product.author && (
            <p className="mt-5 text-[11px] tracking-[0.28em] uppercase text-white/40">
              di {product.author}
            </p>
          )}

          {/* Prezzo */}
          <div
            data-testid="time-book-price-box"
            className="mt-10 flex items-baseline gap-4 flex-wrap"
          >
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] tracking-[0.32em] uppercase text-white/40">Digitale</span>
              <span
                data-testid="time-book-price-digital"
                className="text-4xl sm:text-5xl font-light text-amber-400 tabular-nums"
                style={{ textShadow: "0 0 24px rgba(212,175,55,0.25)" }}
              >
                {formatEuro(product.price)}
              </span>
            </div>
            <div className="text-white/25 text-sm">·</div>
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] tracking-[0.32em] uppercase text-white/40">Cartaceo</span>
              <span
                data-testid="time-book-price-paperback"
                className="text-lg font-light text-white/70 tabular-nums"
              >
                {formatEuro(product.paperbackPrice)}
              </span>
              <span className="text-[10px] tracking-[0.24em] uppercase text-white/30">su Amazon</span>
            </div>
          </div>

          {/* CTA principale */}
          <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <button
              data-testid="time-book-buy-now"
              type="button"
              onClick={handleBuyNow}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-emerald-400 text-black hover:bg-emerald-300 transition-colors text-[12px] tracking-[0.28em] uppercase font-medium shadow-[0_10px_40px_-10px_rgba(16,185,129,0.6)]"
            >
              <ShoppingBag className="w-4 h-4" />
              Acquista ora
            </button>
            {alreadyInCart && (
              <Link
                to="/carrello"
                data-testid="time-book-open-cart"
                className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 transition-colors text-[11px] tracking-[0.28em] uppercase font-mono"
              >
                <Check className="w-3.5 h-3.5" />
                Nel carrello · Vai
              </Link>
            )}
          </div>

          {/* Chip highlights */}
          <div className="mt-8 flex flex-wrap items-center gap-2">
            <Chip icon={<BookOpen className="w-3.5 h-3.5" />} label={product.format} />
            <Chip icon={<Sparkles className="w-3.5 h-3.5" />} label={`Versione ${product.version}`} />
            <Chip icon={<InfinityIcon className="w-3.5 h-3.5" />} label="Aggiornamenti inclusi" />
          </div>
        </div>
      </div>

      {/* Descrizione principale */}
      <section
        data-testid="time-book-description"
        className="mt-16 sm:mt-20 max-w-3xl mx-auto text-center"
      >
        <p className="text-lg sm:text-xl text-white/80 font-light leading-relaxed">
          TIME è un libro che <span className="text-emerald-300">non finirà mai</span>.
        </p>
        <p className="mt-4 text-base sm:text-lg text-white/60 font-light leading-relaxed">
          Non nasce per essere concluso, ma per evolversi nel Tempo.
          <br />
          Capitolo dopo capitolo. Versione dopo versione. Lettura dopo lettura.
        </p>
        <p className="mt-4 text-base sm:text-lg text-white/60 font-light leading-relaxed">
          È un'opera viva, che cresce insieme a chi la legge.
        </p>
      </section>

      {/* Cosa troverai */}
      <section className="mt-16 sm:mt-20 max-w-4xl mx-auto">
        <SectionEyebrow>Cosa troverai</SectionEyebrow>
        <h2 className="mt-4 text-2xl sm:text-3xl font-extralight tracking-tight text-white">
          Riflessioni semplici ma profonde sul valore più importante che possiedi.
        </h2>
        <ul
          data-testid="time-book-contents-list"
          className="mt-8 grid sm:grid-cols-2 gap-3 sm:gap-4"
        >
          {[
            "Come vivi il Tempo.",
            "Perché spesso ti sfugge.",
            "Cosa lasci mentre passa.",
            "Come trasformarlo in direzione.",
            "Come restituirgli significato.",
          ].map((line) => (
            <li
              key={line}
              className="flex items-start gap-3 px-4 py-3 rounded-xl bg-white/[0.02] border border-white/5 text-white/70 font-light"
            >
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0 shadow-[0_0_10px_rgba(16,185,129,0.7)]" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
        <p className="mt-8 text-white/60 font-light leading-relaxed">
          TIME non è un manuale tecnico. Non è filosofia complicata.
          <br />
          <span className="text-white/80">È Tempo trasformato in parole chiare.</span>
        </p>
      </section>

      {/* Versione attuale */}
      <section className="mt-16 sm:mt-20 max-w-4xl mx-auto">
        <SectionEyebrow>Versione attuale</SectionEyebrow>
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <EditionCard n={1} title="Edizione 1" />
          <EditionCard n={2} title="Edizione 2" />
        </div>
        <p className="mt-6 text-white/50 font-light italic leading-relaxed">
          Ogni capitolo fa parte di un disegno più grande destinato a crescere nel Tempo.
        </p>
      </section>

      {/* Libreria — Anteprima + Prima + Seconda Edizione */}
      <LibrarySection
        product={product}
        onAddToCart={() => addItem(product, 1)}
      />

      {/* Un libro che cresce */}
      <section className="mt-16 sm:mt-20 max-w-4xl mx-auto">
        <SectionEyebrow>Un libro che cresce</SectionEyebrow>
        <div
          data-testid="time-book-growth"
          className="mt-6 p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-emerald-500/[0.06] via-white/[0.02] to-transparent border border-emerald-400/15"
        >
          <p className="text-white/80 font-light leading-relaxed">
            Acquistando TIME in formato digitale riceverai:
          </p>
          <ul className="mt-4 space-y-2 text-white/70 font-light">
            {[
              "la versione completa attuale;",
              "tutti gli aggiornamenti futuri inclusi;",
              "ogni nuovo capitolo senza costi aggiuntivi.",
            ].map((line) => (
              <li key={line} className="flex items-start gap-3">
                <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-white/80 font-light">
            Lo acquisti una volta.
            <br />
            <span className="text-emerald-300">Poi sarà il Tempo a farlo crescere.</span>
          </p>
        </div>
      </section>

      {/* Dettagli */}
      <section className="mt-16 sm:mt-20 max-w-4xl mx-auto">
        <SectionEyebrow>Dettagli</SectionEyebrow>
        <dl
          data-testid="time-book-details"
          className="mt-6 grid sm:grid-cols-2 gap-3"
        >
          <DetailRow label="Formato digitale" value="PDF" />
          <DetailRow label="Versione attuale" value={product.version} />
          <DetailRow label="Aggiornamenti futuri" value="Inclusi" />
          <DetailRow
            label="Versione cartacea"
            value={
              <a
                href={product.paperbackUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200 transition-colors"
                data-testid="time-book-amazon-link"
              >
                Su Amazon <ExternalLink className="w-3 h-3" />
              </a>
            }
          />
        </dl>
      </section>

      {/* Chiusura + CTA finale */}
      <section
        data-testid="time-book-closing"
        className="mt-16 sm:mt-24 max-w-3xl mx-auto text-center"
      >
        <p className="text-xl sm:text-2xl font-light text-white/85 leading-relaxed italic">
          TIME è ciò che senti quando ti fermi davvero.
        </p>
        <p className="mt-4 text-base sm:text-lg text-white/55 font-light leading-relaxed">
          Un libro da leggere e rileggere, perché ogni lettura aggiunge consapevolezza
          e restituisce Tempo.
        </p>
        <button
          data-testid="time-book-buy-now-bottom"
          type="button"
          onClick={handleBuyNow}
          className="mt-10 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-emerald-400 text-black hover:bg-emerald-300 transition-colors text-[12px] tracking-[0.28em] uppercase font-medium shadow-[0_10px_40px_-10px_rgba(16,185,129,0.6)]"
        >
          <ShoppingBag className="w-4 h-4" />
          Acquista ora · {formatEuro(product.price)}
        </button>
      </section>
    </div>
  );
}

function Chip({ icon, label }) {
  return (
    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-[10px] sm:text-[11px] tracking-[0.24em] uppercase text-white/60 font-mono">
      {icon}
      {label}
    </span>
  );
}

function SectionEyebrow({ children }) {
  return (
    <div className="text-[11px] tracking-[0.32em] uppercase text-emerald-400/70">
      {children}
    </div>
  );
}

function EditionCard({ n, title }) {
  return (
    <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/5 flex items-center gap-4">
      <div className="w-10 h-10 rounded-full flex items-center justify-center bg-emerald-400/10 border border-emerald-400/25 text-emerald-300 font-mono text-sm">
        {n}
      </div>
      <div>
        <div className="text-white font-light">{title}</div>
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/40 mt-0.5">
          Inclusa
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3 rounded-xl bg-white/[0.02] border border-white/5">
      <dt className="text-[10px] tracking-[0.28em] uppercase text-white/45 font-mono">{label}</dt>
      <dd className="text-sm text-white/85 font-light text-right">{value}</dd>
    </div>
  );
}
